import React from 'react'
import { MobileScheduleDay } from '../ConsensusEventMobile'
import { ScheduleDayProps } from '../types'

export const ScheduleDay: React.FC<ScheduleDayProps> = ({
  id,
  title,
  headers,
  rows,
  gridCols
}) => {
  const gridColVariants: { [key: number]: string } = {
    6: 'grid-cols-[minmax(11rem,_auto)_repeat(5,_minmax(0,_1fr))]',
    8: 'grid-cols-[minmax(11rem,_auto)_repeat(7,_minmax(0,_1fr))]'
  }

  const colSpanVariants: { [key: number]: string } = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7'
  }

  const gridColsClass = gridColVariants[gridCols]

  return (
    <div id={id}>
      {/* Mobile Version */}
      <MobileScheduleDay
        title={title}
        headers={headers}
        rows={rows}
        gridCols={gridCols}
      />

      {/* Desktop Version */}
      <div className="hidden md:block bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-2xl font-serif text-[#3C2416] mb-4">{title}</h3>
        <div
          className={`grid ${gridColsClass} w-full text-sm border-l border-t border-gray-300`}
        >
          {/* Headers */}
          {headers.map((header, index) => (
            <div
              key={`header-track-${index}`}
              className="text-center py-2 px-2 font-bold border-r border-b border-gray-300 flex items-center justify-center"
              style={{
                backgroundColor: header.color,
                color: header.textColor || '#3C2416'
              }}
            >
              {header.name}
            </div>
          ))}
          {headers.map((header, index) => (
            <div
              key={`header-location-${index}`}
              className="text-center py-1 px-2 font-semibold border-r border-b border-gray-300"
              style={{
                backgroundColor: '#142841',
                color: 'white'
              }}
            >
              {header.location || ''}
            </div>
          ))}

          {/* Rows */}
          {rows.flatMap((row) => {
            const cells = []
            let sessionColumn = 1 // Start at 1 to account for time column

            // Time cell
            cells.push(
              <div
                key={`time-${row.time}`}
                className="py-2 px-2 border-r border-b border-gray-300 font-bold text-center"
                style={{ backgroundColor: '#142841', color: 'white' }}
              >
                {row.time}
              </div>
            )

            // Session cells
            for (const [sessionIndex, session] of row.sessions.entries()) {
              const colSpan = session.span || 1
              const colSpanClass = colSpan > 1 ? colSpanVariants[colSpan] : ''
              const headerForColor = headers[sessionColumn]
              const boldClass = session.isBold ? 'font-bold' : ''
              const centerClass = session.isCentered ? 'text-center' : ''

              // Function to render text with bold room references
              const renderTextWithBoldRooms = (text: string) => {
                const roomRegex = /\(in [^)]+\)/g
                return text.replace(roomRegex, '<strong>$&</strong>')
              }

              // Check if this is a Thursday morning Pinnacle session
              const isThursdayMorningPinnacle =
                title.includes('Thursday') &&
                sessionColumn === 1 && // Pinnacle is the first column after time
                (row.time.includes('9:00') ||
                  row.time.includes('10:00') ||
                  row.time.includes('10:45') ||
                  row.time.includes('11:15')) &&
                row.time.includes('AM') &&
                session.text !== 'N/A' &&
                !session.text.toLowerCase().includes('breakfast')

              cells.push(
                <div
                  key={`session-${row.time}-${sessionIndex}`}
                  className={`py-2 px-2 border-r border-b border-gray-300 ${colSpanClass} ${boldClass} ${centerClass} flex flex-col justify-center`}
                  style={{
                    backgroundColor:
                      session.text === 'N/A'
                        ? '#EDE6DE'
                        : session.text.includes('Sanofi Diamond Lecture')
                          ? '#A0B2B4'
                          : isThursdayMorningPinnacle
                            ? '#A0B2B4'
                            : session.span && session.span > 1
                              ? 'white'
                              : headerForColor.color,
                    color:
                      session.text === 'N/A'
                        ? '#9298a8'
                        : session.span && session.span > 1
                          ? '#3C2416'
                          : headerForColor.textColor || '#3C2416'
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderTextWithBoldRooms(session.text)
                    }}
                  />
                  {session.speaker && (
                    <div className="text-xs italic mt-1 opacity-80">
                      {Array.isArray(session.speaker)
                        ? session.speaker.join(', ')
                        : session.speaker}
                    </div>
                  )}
                </div>
              )
              sessionColumn += colSpan
            }

            return cells
          })}
        </div>
      </div>
    </div>
  )
}
